/*
 * Dummy data for the LTR dashboard, used when mock mode is on (see LTRService).
 *
 * This file exists so the screen can be reviewed before api-ltr-service is
 * deployed. It is inert unless mock mode is explicitly switched on, and the
 * whole folder can be deleted once the real service is live.
 *
 * The dataset is 82 cases, matching the stat tiles in the design:
 *   82 total = 12 new leads + 18 in progress + 52 closed
 *   52 closed = 30 successful + 5 unsuccessful + 7 not interested + 10 false positive
 *
 * The ten newest cases (page one) carry the exact rows from the design, so
 * every visual state is on screen at once: all three statuses, all four closure
 * outcomes, the "+ Set action" CTA, the empty "—" action on a new lead, and
 * every user-type colour.
 *
 * Generation is deterministic — a seeded PRNG, no Math.random and no Date.now —
 * so the table looks identical on every reload and screenshots are comparable.
 */

// Employee ids are arbitrary but stable, so the "Assigned to" filter has a
// consistent roster to select from.
export const MOCK_ASSIGNEES = [
  { id: 1869, name: "Dr Shweta", role: "doctor" },
  { id: 1870, name: "Dr Trishla", role: "doctor" },
  { id: 1871, name: "Ankita", role: "coordinator" },
  { id: 1872, name: "Priyanka", role: "coordinator" },
  { id: 1873, name: "Dr Rohan", role: "doctor" },
  { id: 1874, name: "Meera", role: "coordinator" },
];

const ASSIGNEE_BY_ID = MOCK_ASSIGNEES.reduce((acc, a) => {
  acc[a.id] = a;
  return acc;
}, {});

export const MOCK_TRIGGERS = [
  "Lab Values",
  "Heart Rate",
  "Routine Check",
  "BP Critical",
  "Cardiac Event",
  "Sepsis Alert",
  "Post-Op",
  "Hypoxia",
  "Arrhythmia",
  "Renal Failure",
  "Stroke Alert",
  "Diabetic Ketoacidosis",
];

export const MOCK_ACCOUNT_CODES = [
  "01XXDC",
  "AT12345",
  "ER76123",
  "LK32109",
  "JH21098",
  "CV65432",
];

const STATUS_NEW_LEAD = 1;
const STATUS_IN_PROGRESS = 2;
const STATUS_CLOSED = 3;

const OUTCOME_SUCCESSFUL = 1;
const OUTCOME_UNSUCCESSFUL = 2;
const OUTCOME_FALSE_POSITIVE = 3;
const OUTCOME_USER_NOT_INTERESTED = 4;

// Linear congruential generator — same constants as Numerical Recipes. Seeded
// so the dataset never shifts between reloads.
function makeRng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

const pick = (rng, pool) => pool[Math.floor(rng() * pool.length)];

// A doctor can mark several actions on one case, so seed a mix of single- and
// multi-action rows.
function pickActionSet(rng) {
  const all = [1, 2, 3, 4, 5, 6];
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [all[i], all[j]] = [all[j], all[i]];
  }
  return all.slice(0, 1 + Math.floor(rng() * 3)).sort((a, b) => a - b);
}
const pickInt = (rng, min, max) => min + Math.floor(rng() * (max - min + 1));

// Builds an ISO timestamp from a day offset before the anchor plus a time of
// day, so cases spread backwards through the calendar the way a real queue does.
const ANCHOR = Date.UTC(2026, 7, 25); // 25 Aug 2026, the newest stratification

function at(daysBeforeAnchor, hour, minute) {
  const d = new Date(ANCHOR - daysBeforeAnchor * 86400000);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Page one — the exact rows from the design.
// ---------------------------------------------------------------------------
// seq counts down from 82 so these are both the newest cases and the highest
// ids, which is what the real service produces (the id sequence and the
// stratification date always advance together).
const HERO_ROWS = [
  {
    seq: 82, patientProfileType: 1, patientId: 612508, trigger: "Lab Values",
    source: at(1, 9, 30), strat: at(0, 11, 0), followUp: at(-3, 11, 0),
    assignee: 1872, calls: 2, actions: [1],
    status: STATUS_CLOSED, outcome: OUTCOME_USER_NOT_INTERESTED,
  },
  {
    seq: 81, patientProfileType: 2, patientId: 453981, trigger: "Heart Rate",
    source: at(2, 7, 10), strat: at(1, 11, 0), followUp: at(-2, 11, 0),
    assignee: 1869, calls: 2, actions: [],
    status: STATUS_IN_PROGRESS,
  },
  {
    seq: 80, patientProfileType: 3, patientId: 286430, trigger: "Routine Check",
    source: at(2, 8, 15), strat: at(1, 11, 0), followUp: at(-2, 11, 0),
    assignee: 1871, calls: 3, actions: [5],
    status: STATUS_IN_PROGRESS,
  },
  {
    seq: 79, patientProfileType: 4, patientId: 907362, trigger: "BP Critical",
    source: at(3, 9, 45), strat: at(2, 11, 0), followUp: at(-1, 11, 0),
    assignee: 1870, calls: 1, actions: [1, 4],
    status: STATUS_CLOSED, outcome: OUTCOME_FALSE_POSITIVE,
  },
  {
    seq: 78, patientProfileType: 0, patientId: 128495, trigger: "Cardiac Event",
    source: at(3, 6, 30), strat: at(2, 11, 0), followUp: at(-1, 11, 0),
    assignee: 1870, calls: 0, actions: [],
    status: STATUS_NEW_LEAD,
  },
  {
    seq: 77, patientProfileType: 1, patientId: 295817, trigger: "Sepsis Alert",
    source: at(4, 10, 20), strat: at(3, 11, 0), followUp: at(0, 11, 0),
    assignee: 1869, calls: 6, actions: [4],
    status: STATUS_IN_PROGRESS,
  },
  {
    seq: 76, patientProfileType: 4, patientId: 839271, trigger: "Lab Values",
    source: at(4, 9, 5), strat: at(3, 11, 0), followUp: at(0, 11, 0),
    assignee: 1869, calls: 2, actions: [3],
    status: STATUS_CLOSED, outcome: OUTCOME_UNSUCCESSFUL,
  },
  {
    seq: 75, patientProfileType: 0, patientId: 174529, trigger: "Heart Rate",
    source: at(5, 7, 55), strat: at(4, 11, 0), followUp: at(1, 11, 0),
    assignee: 1869, calls: 0, actions: [],
    status: STATUS_NEW_LEAD,
  },
  {
    seq: 74, patientProfileType: 3, patientId: 740196, trigger: "Post-Op",
    source: at(5, 8, 40), strat: at(4, 11, 0), followUp: at(1, 11, 0),
    assignee: 1871, calls: 3, actions: [2, 6],
    status: STATUS_IN_PROGRESS,
  },
  {
    seq: 73, patientProfileType: 4, patientId: 121334, trigger: "BP Critical",
    source: at(5, 9, 15), strat: at(4, 11, 0), followUp: at(1, 11, 0),
    assignee: 1871, calls: 4, actions: [1],
    status: STATUS_CLOSED, outcome: OUTCOME_SUCCESSFUL,
  },
];

// ---------------------------------------------------------------------------
// The remaining 72 cases, generated to top the totals up to the design's tiles.
// ---------------------------------------------------------------------------
const repeat = (value, n) => Array.from({ length: n }, () => value);

// Page one already accounts for 2 new leads, 4 in progress and 4 closed
// (one of each outcome), so the filler makes up the difference.
const FILLER_DISPOSITIONS = [
  ...repeat({ status: STATUS_NEW_LEAD }, 10),
  ...repeat({ status: STATUS_IN_PROGRESS }, 14),
  ...repeat({ status: STATUS_CLOSED, outcome: OUTCOME_SUCCESSFUL }, 29),
  ...repeat({ status: STATUS_CLOSED, outcome: OUTCOME_UNSUCCESSFUL }, 4),
  ...repeat({ status: STATUS_CLOSED, outcome: OUTCOME_USER_NOT_INTERESTED }, 6),
  ...repeat({ status: STATUS_CLOSED, outcome: OUTCOME_FALSE_POSITIVE }, 9),
];

// Deterministic Fisher-Yates, so open and closed cases interleave down the
// pages instead of arriving in blocks.
function shuffled(items, rng) {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function buildCase({
  seq, patientProfileType, patientId, trigger, extraTrigger, source, strat, followUp,
  assignee, calls, actions, status, outcome, accountCode, consentGiven = true,
}) {
  const person = ASSIGNEE_BY_ID[assignee];
  const triggers = extraTrigger ? [trigger, extraTrigger] : [trigger];
  const isClosed = status === STATUS_CLOSED;

  return {
    // A 24-char hex id, the shape the real service returns, so anything that
    // treats the id as a Mongo ObjectID keeps working.
    id: `ffffffffffffffff${String(seq).padStart(8, "0")}`,
    ltrId: `LTR-${seq}`,
    ltrSeq: seq,
    patientId,
    accountCode,
    patientProfileType,
    riskRecordId: `eeeeeeeeeeeeeeee${String(seq).padStart(8, "0")}`,
    riskLevel: 1,
    riskLevelTriggeredBy: triggers,
    sourceDataDate: source,
    riskStratificationDate: strat,
    followUpDate: followUp,
    assignedToEmployeeId: assignee,
    assignedToRole: person ? person.role : "",
    assignedToName: person ? person.name : "",
    callCount: calls,
    actions: actions && actions.length ? actions : undefined,
    status,
    closureOutcome: isClosed ? outcome : undefined,
    closureReason: isClosed ? "Closed from the case detail screen." : undefined,
    consentGiven,
    createdAt: strat,
    updatedAt: strat,
  };
}

let cachedCases = null;

export function getMockCases() {
  if (cachedCases) return cachedCases;

  const rng = makeRng(20260825);
  const cases = HERO_ROWS.map((row, i) =>
    buildCase({ ...row, accountCode: MOCK_ACCOUNT_CODES[i % MOCK_ACCOUNT_CODES.length] }),
  );

  const dispositions = shuffled(FILLER_DISPOSITIONS, rng);
  dispositions.forEach((disposition, i) => {
    const seq = 72 - i;
    // Filler cases march backwards from the day after the oldest hero row.
    const dayOffset = 6 + i;
    const trigger = pick(rng, MOCK_TRIGGERS);
    // Every seventh case gets a second trigger, so the column's wrapping and
    // truncation get exercised rather than only ever holding one short label.
    const extraTrigger =
      i % 7 === 3 ? MOCK_TRIGGERS[(MOCK_TRIGGERS.indexOf(trigger) + 5) % MOCK_TRIGGERS.length] : null;

    const isNewLead = disposition.status === STATUS_NEW_LEAD;
    // Every third new lead has no contact consent: intake left it with no
    // doctor and no follow-up date, waiting for someone to take it.
    const noConsent = isNewLead && i % 3 === 1;
    const assignee = pick(rng, MOCK_ASSIGNEES).id;
    cases.push(
      buildCase({
        seq,
        patientProfileType: pickInt(rng, 0, 4),
        patientId: pickInt(rng, 100000, 999999),
        trigger,
        extraTrigger,
        source: at(dayOffset + 1, pickInt(rng, 6, 11), pickInt(rng, 0, 59)),
        strat: at(dayOffset, 11, 0),
        followUp: noConsent ? null : at(dayOffset - 3, 11, 0),
        assignee: noConsent ? 0 : assignee,
        // A new lead has not been called yet, which is exactly why its action
        // cell is empty rather than offering the "+ Set action" CTA.
        calls: isNewLead ? 0 : pickInt(rng, 1, 7),
        actions: isNewLead ? [] : pickActionSet(rng),
        status: disposition.status,
        outcome: disposition.outcome,
        accountCode: pick(rng, MOCK_ACCOUNT_CODES),
        consentGiven: !noConsent,
      }),
    );
  });

  cachedCases = cases;
  return cachedCases;
}

// ---------------------------------------------------------------------------
// Query — mirrors buildMatch in repositories/ltrCaseRepo.go so the filters
// behave here the way they will against the real service.
// ---------------------------------------------------------------------------

// An empty value set is no constraint regardless of mode — that is how
// "select everything", which travels as (exclude, []), collapses to no filter.
function matchesList(value, filter) {
  if (!filter || !filter.values || filter.values.length === 0) return true;
  const present = Array.isArray(value)
    ? value.some((v) => filter.values.includes(v))
    : filter.values.includes(value);
  return filter.mode === "exclude" ? !present : present;
}

// A half-set range is no constraint, matching the BE. A case with no date for
// the column can never fall inside a set range.
function matchesRange(value, range) {
  if (!range || !range.from || !range.to) return true;
  if (!value) return false;
  const t = new Date(value).getTime();
  return t >= new Date(range.from).getTime() && t <= new Date(range.to).getTime();
}

function applyFilters(cases, body = {}) {
  return cases.filter((c) => {
    if (body.patientId && c.patientId !== body.patientId) return false;
    if (body.ltrId) {
      const q = String(body.ltrId).trim().toLowerCase();
      if (q && !c.ltrId.toLowerCase().includes(q)) return false;
    }
    // A plain list, like the service's profileTypes; empty is no constraint.
    if (body.profileTypes && body.profileTypes.length && !body.profileTypes.includes(c.patientProfileType)) return false;
    if (!matchesList(c.accountCode, body.accountCodes)) return false;
    if (!matchesList(c.riskLevelTriggeredBy, body.riskLevelTriggeredBy)) return false;
    if (!matchesList(c.assignedToEmployeeId, body.assigneeIds)) return false;
    if (!matchesList(c.actions, body.actions)) return false;
    if (!matchesList(c.status, body.statuses)) return false;
    if (!matchesList(c.closureOutcome, body.outcomes)) return false;
    if (!matchesRange(c.sourceDataDate, body.sourceDataDate)) return false;
    if (!matchesRange(c.riskStratificationDate, body.riskStratificationDate)) return false;
    if (!matchesRange(c.followUpDate, body.followUpDate)) return false;
    if (typeof body.consentGiven === "boolean" && !!c.consentGiven !== body.consentGiven) return false;
    return true;
  });
}

// Newest first, with the sequence as the tiebreaker — same sort the repository
// applies, so paging is stable.
function sortCases(cases) {
  return cases.slice().sort((a, b) => {
    const diff =
      new Date(b.riskStratificationDate) - new Date(a.riskStratificationDate);
    return diff !== 0 ? diff : b.ltrSeq - a.ltrSeq;
  });
}

export function queryMockCases(body = {}) {
  const matched = sortCases(applyFilters(getMockCases(), body));
  const pageNum = body.pageNum > 0 ? body.pageNum : 1;
  const pageSize = body.pageSize > 0 ? body.pageSize : 20;
  const start = (pageNum - 1) * pageSize;
  const page = matched.slice(start, start + pageSize);

  return {
    response: page,
    count: page.length,
    totalCount: matched.length,
  };
}

export function statMockCases(body = {}) {
  const matched = applyFilters(getMockCases(), body);
  const countBy = (fn) => matched.filter(fn).length;
  const closedWith = (outcome) => (c) =>
    c.status === STATUS_CLOSED && c.closureOutcome === outcome;

  return {
    totalCases: matched.length,
    // Like the service: only new leads with contact consent.
    newLeads: countBy((c) => c.status === STATUS_NEW_LEAD && c.consentGiven),
    inProgress: countBy((c) => c.status === STATUS_IN_PROGRESS),
    closed: countBy((c) => c.status === STATUS_CLOSED),
    closedSuccessfully: countBy(closedWith(OUTCOME_SUCCESSFUL)),
    closedUnsuccessfully: countBy(closedWith(OUTCOME_UNSUCCESSFUL)),
    userNotInterested: countBy(closedWith(OUTCOME_USER_NOT_INTERESTED)),
    falsePositive: countBy(closedWith(OUTCOME_FALSE_POSITIVE)),
  };
}

// Mirrors the q/offset/limit contract the real option endpoints serve.
export function mockFilterOptions(all, { q = "", offset = 0, limit = 50 } = {}) {
  const needle = String(q || "").trim().toLowerCase();
  const filtered = needle
    ? all.filter((o) => o.label.toLowerCase().includes(needle))
    : all;
  const start = Math.min(offset, filtered.length);
  const end = limit > 0 ? Math.min(start + limit, filtered.length) : filtered.length;
  return {
    options: filtered.slice(start, end),
    total: filtered.length,
    hasMore: end < filtered.length,
  };
}

export function mockAssigneeOptions() {
  return MOCK_ASSIGNEES.map((a) => ({ id: a.id, label: a.name })).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

export function mockTriggerOptions() {
  return MOCK_TRIGGERS.slice().sort().map((t) => ({ id: t, label: t }));
}

export function mockAccountCodeOptions() {
  return MOCK_ACCOUNT_CODES.slice().sort().map((c) => ({ id: c, label: c }));
}
